import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from '../../lib/cn';
import { brand } from '../../data/media';
import site from '../../data/site';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';

const SEEN_KEY = 'rj:intro-seen';
const MAX_MS = 5200; // Hard stop, in case the clip stalls or never fires `ended`.

/**
 * A one-off brand sting on first visit, built from the supplied logo intro
 * clip. It shows at most once per browser session, is skippable at any time,
 * and never appears for visitors who prefer reduced motion.
 */
export function IntroCurtain() {
  const reduceMotion = usePrefersReducedMotion();

  // Decide synchronously so the curtain never flashes for a repeat visitor.
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(SEEN_KEY) !== '1';
    } catch {
      return false; // Storage blocked — skip rather than replay on every route.
    }
  });
  const [leaving, setLeaving] = useState(false);
  const videoRef = useRef(null);

  const active = show && !reduceMotion;
  useLockBodyScroll(active);

  const dismiss = useCallback(() => {
    setLeaving(true);
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* non-fatal */
    }
    window.setTimeout(() => setShow(false), 700);
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const video = videoRef.current;
    if (video) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => dismiss());
    }

    const timer = window.setTimeout(dismiss, MAX_MS);
    const onKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') dismiss();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, dismiss]);

  // Mark as seen even when we skip it, so the flag is consistent.
  useEffect(() => {
    if (show && reduceMotion) {
      try {
        window.sessionStorage.setItem(SEEN_KEY, '1');
      } catch {
        /* non-fatal */
      }
      setShow(false);
    }
  }, [show, reduceMotion]);

  if (!active) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] grid place-items-center bg-ink-950 transition-opacity duration-700 ease-silk',
        leaving ? 'pointer-events-none opacity-0' : 'opacity-100'
      )}
      role="presentation"
    >
      <video
        ref={videoRef}
        src={brand.intro}
        className="h-full w-full max-w-md object-contain"
        muted
        playsInline
        autoPlay
        onEnded={dismiss}
        onError={dismiss}
        aria-label={`${site.shortName} introduction`}
      />

      <button
        type="button"
        onClick={dismiss}
        className="absolute bottom-10 right-8 text-[0.65rem] uppercase tracking-mega text-canvas/50 transition hover:text-champagne-300"
      >
        Skip
      </button>
    </div>
  );
}

export default IntroCurtain;
